/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState, useMemo } from 'react'
import DataTable from '../../../../components/DataTable/DataTable'
import { Link } from 'react-router-dom/cjs/react-router-dom';
import { Button, Loading, Tag, Text, TextInput, Tooltip } from '@sede-enterprise/shell-ds-react-framework';
import useFetchSensorList from '../../hooks/useFetchSensorList';
import { UnitContext } from '../../../../App';
import { Download, Search } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { toast } from 'react-toastify';
import useDebounce from '../../../../util/useDebounce';
import { downloadAsExcel, mmOrMiles } from '../../../../util/helperFunctions';
import { ratingSentimentMap } from '../../../../util/constants';
import { DE_INSTALL_HELP, DE_INSTALLED_PROD_UNIT } from '../../../../util/helperTexts';
import * as R from "rambda";


const pageSize = 10;
const INIT_PAGE = 1;
const INIT_ORDER = "latestCorrosionRatePredictionFromLiveModel";
const INIT_ORDER_DIRECTION = 1
const sensor_filter = `sensor.excludeFromUi = 0`;

export const downloadAsXLSX = async (sensorData, columns) => {
    if (sensorData?.sensorList?.length && columns?.length > 0) {
        const downloadData = sensorData?.sensorList?.map((ele) => {
            return columns.reduce(
                (prev, curr) => {
                    const a = curr.id.split(".");
                    const item = a.reduce((p, c) => {
                        if (p) {
                            return p[c];
                        } else {
                            return "";
                        }
                    }, ele);
                    return {
                        ...prev,
                        [curr.header]: item,
                    };
                },
                {
                    [columns[0].header]:
                        ele?.site,
                }
            );
        });

        await downloadAsExcel([{ sheetName: 'SensorList', data: downloadData }], 'SensorList.xlsx');
    }
    
};

const SensorDataList = ({ filter }) => {

    const context = useContext(UnitContext);
    const [currentPage, setCurrentPage] = useState(+sessionStorage.getItem("sensorPage") || INIT_PAGE);
    const [sensorOrder, setSensorOrder] = useState(sessionStorage.getItem("sensorOrder") || INIT_ORDER);
    const [orderDirection, setOrderDirection] = useState(+sessionStorage.getItem("sensororderDirection") || INIT_ORDER_DIRECTION);
    const [prevFilter, setPrevFilter] = useState(filter);

    const [searchVal, setSearchVal] = useState("");
    const debouncedSearchVal = useDebounce(searchVal, 500);

    const [sensorListFilter, setSensorListFilter] = useState(sessionStorage.getItem("sensorApplyfilter") || sensor_filter);

    useEffect(() => {

        if (prevFilter !== filter) {
            setCurrentPage(INIT_PAGE);
            sessionStorage.removeItem('sensorPage');
        }
        setPrevFilter(filter);
    }, [filter]);

    useEffect(() => {
        if (debouncedSearchVal) {
            const appliedSensorFilter = sessionStorage.getItem("sensorApplyfilter");
            const filterValue = appliedSensorFilter
                ? `${appliedSensorFilter} AND (UPPER(sensor.name) LIKE UPPER('%${debouncedSearchVal}%') OR UPPER(sensor.conditionMonitoringLocation) LIKE UPPER('%${debouncedSearchVal}%')) AND ${sensor_filter}`
                : `(UPPER(sensor.name) LIKE UPPER('%${debouncedSearchVal}%') OR UPPER(sensor.conditionMonitoringLocation) LIKE UPPER('%${debouncedSearchVal}%')) AND ${sensor_filter}`;
            setSensorListFilter(filterValue);
            setCurrentPage(INIT_PAGE); // Reset the current page
            sessionStorage.removeItem('sensorPage'); // Remove the current page from local storage
        }
        else {
            setSensorListFilter(filter);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchVal, filter]);

    const handleOrderChange = (sortBy, direction) => {
        setSensorOrder(sortBy);
        sessionStorage.setItem('sensorOrder', sortBy);

        setOrderDirection(+direction);
        sessionStorage.setItem('sensororderDirection', +direction);
    }

    const handleCurrentPageChange = (current) => {
        sessionStorage.setItem('sensorPage', current)
        setCurrentPage(current);
    }

    const orderString = orderDirection === 1 ? `DESC` : `ASC`;
    const { data: sensorData, error, loading } = useFetchSensorList(sensorListFilter, pageSize, currentPage, sensorOrder, orderString);
    const { data: sensorDownLoadData, error: downloadError, loading: downloadLoading } = useFetchSensorList(sensorListFilter, 10000, 1, sensorOrder, orderString);
    /** @type import('@tanstack/react-table').ColumnDef<any> */
    
        const columns = [
        {
            header: 'Site',
            accessorFn: row => row.site,
            id: 'site',
        },
        {
            header: 'Prod Unit',
            accessorFn: row => row.productionUnit,
            id: 'productionUnit',
        },
        {
            header: 'Unit',
            accessorFn: row => row.unit,
            id: 'unit',
        },
        {
            header: 'Equipment',
            accessorFn: row => row.equipment,
            id: 'equipment',
        },
        {
            header: 'Corrosion Loop',
            accessorFn: row => row.corrosionLoop,
            cell: (props) => {
                const row = props.row.original;
                return row.corrosionLoop ? (
                    <Link
                        className="link"
                        to={`/cls/${encodeURIComponent(row.corrosionLoop)}/details`}
                        id='sensor-list-id-2'
                    >
                        {row.corrosionLoop}
                    </Link>
                ) : null
            },
            id: 'corrosionLoop',
        },
        {
            header: 'CML',
            accessorFn: row => row.conditionMonitoringLocation,
            cell: (props) => {
                const row = props.row.original;
                return row.conditionMonitoringLocation ? (
                    <Link
                        className="link"
                        to={`/cmls/${encodeURIComponent(row.cmLocation)}/details`}
                        id='sensor-list-id-1'
                    >
                        {row.conditionMonitoringLocation}
                    </Link>
                ) : null
            },
            id: 'conditionMonitoringLocation',
        },
        {
            header: 'Sensor',
            accessorFn: row => row.name,
            cell: (props) => {
                const row = props.row.original;
                return row.name ? (
                    <Link
                        className={
                            row.uninstalled ? "link redContent" : "link blueContent"
                        }
                        to={`/home/${encodeURIComponent(row.id)}/details`}
                        id='sensor-list-id-3'
                    >
                        {row.isRealSensor && row.name}
                        {!row.isRealSensor && (
                            <span
                                title="This CML has no Sensor installed"
                                className="redContent"
                                id='sensor-list-id-4'
                            > No Sensor
                            </span>
                        )}
                    </Link>
                ) : null
            },
            id: 'name',
        },
        {
            header: "Latest PCR (" + mmOrMiles(context.units) + ")",
            cell: (props) => {
                const row = props.row.original;
                return context.units
                    ? R.path(["latestCorrosionRatePredictionFromLiveModelMilsPYear"], row)?.toFixed(
                        3) : R.path(["latestCorrosionRatePredictionFromLiveModel"], row)?.toFixed(
                            3)
            },
            accessorFn: (row) =>
                context.units
                    ? R.path(["latestCorrosionRatePredictionFromLiveModelMilsPYear"], row)?.toFixed(
                        3) : R.path(["latestCorrosionRatePredictionFromLiveModel"], row)?.toFixed(
                            3),
            id: context.units ? "latestCorrosionRatePredictionFromLiveModelMilsPYear" : "latestCorrosionRatePredictionFromLiveModel",
        },
        {
            header: "Average PCR (" + mmOrMiles(context.units) + ")",
            cell: (props) => {
                const row = props.row.original;
                return context.units
                    ? R.path(["averageCorrosionRatePredictionFromLiveModelMilsPYear"], row)?.toFixed(
                        3) : R.path(["averageCorrosionRatePredictionFromLiveModel"], row)?.toFixed(
                            3)
            },
            accessorFn: (row) =>
                context.units
                    ? R.path(["averageCorrosionRatePredictionFromLiveModelMilsPYear"], row)?.toFixed(
                        3) : R.path(["averageCorrosionRatePredictionFromLiveModel"], row)?.toFixed(
                            3),
            id: context.units ? "averageCorrosionRatePredictionFromLiveModelMilsPYear" : "averageCorrosionRatePredictionFromLiveModel",
        },
        {
            header: 'Rating',
            cell: (props) => {
                const row = props.row.original;
                return (
                    <Tag
                        sentiment={ratingSentimentMap[row.sensorRating]}
                        variant='outlined'
                        size='small'
                        shape='round'
                        id='sensor-list-id-5'
                    >
                        {row.sensorRating}
                    </Tag>
                )
            },
            id: "sensorRating",
            accessorFn: (row) => row.sensorRating
        },
        {
            header: 'Fluid',
            accessorFn: row => row.fluid,
            id: 'fluid',
        },
        {
            header: 'Material',
            accessorFn: row => row.material,
            id: 'material',
        },
        {
            header: 'Position',
            accessorFn: row => row.position,
            id: 'position',
        },
        ];


    return (
        <div className='parent-container' id='sensor-list-id-6'>
            <div className='content-container' id='sensor-list-id-7'>
                <div className='content-header' id='sensor-list-id-8'>
                    <h2 id='sensor-list-id-9'>Sensor Data List</h2>
                    <div className='header-actions' id='sensor-list-id-10'>
                        <div className='search-wrapper' id='sensor-list-id-11'>
                            <TextInput
                                prefix={{ node: <Search className='search-icon' id='sensor-list-id-12' /> }}
                                placeholder='Search by Sensor or CML name'
                                onChange={(e) => {
                                    setSearchVal(e.target.value);
                                }}
                                value={searchVal}
                                id='sensor-list-id-13'
                            />
                        </div>
                        <Tooltip id='id-5' overlay={<span id='home-id-6'>Download</span>}
                            placement='bottom'
                            trigger={<Button iconOnly icon={<Download />} onClick={() => downloadAsXLSX(sensorDownLoadData, columns)} id='sensor-list-id-14' />} />

                    </div>
                </div>
                <div className='content-body' id='sensor-list-id-15'>
                    {(!!error || (!loading && sensorData?.sensorList?.length === 0)) && <div className='empty-container' id='sensor-list-id-16'>
                        <Text id='sensor-list-id-17'>No Sensors to display</Text>
                    </div>}
                    {sensorData?.sensorList?.length <= 0 && loading && <div className='loading-container' id='sensor-list-id-18' role='status'><Loading id='sensor-list-id-19' /></div>}
                    {sensorData?.sensorList?.length > 0 &&
                        <DataTable data={sensorData?.sensorList} columns={columns} count={sensorData?.sensorCount?.TotalRows}
                            currentPage={+sessionStorage.getItem("sensorPage") || INIT_PAGE}
                            handleCurrentPageChange={handleCurrentPageChange}
                            handleOrderChange={handleOrderChange}
                            INIT_ORDER={sessionStorage.getItem("sensorOrder") || INIT_ORDER}
                            INIT_ORDER_DIRECTION={+sessionStorage.getItem("sensororderDirection") || INIT_ORDER_DIRECTION}
                            loading={loading}
                            // paginationMode='client'
                            id='sensor-list-id-20'
                        />}

                </div>
            </div>
        </div>
    )
}

export default SensorDataList